import { useState } from 'react'
import {
  saveAiCompanyLimits,
  saveAiInflightView,
  type AiAgentShell,
  type AiCompanyRow,
} from '../../api/settingsAiAgent'
import { failed, NoteText, shortNum, type GlobalDraft, type Note } from './parts'

/**
 * Fila "Companies" - numai la Global.
 *
 * CE SE HOTARASTE DESPRE UN CLIENT: are agentul sau nu, cat poate cheltui pe zi,
 * si cat de adanc poate cauta. Pana pe 4 septembrie 2026 ultimele doua stateau
 * in fila de modele, ca doua numere globale, desi unul se numea chiar "per
 * company, per day" - iar toate firmele imparteau limita scrisa intr-un loc
 * unde nu se putea da alta unei firme anume.
 *
 * Lista NU e a tuturor firmelor: sunt cele care au deja o hotarare scrisa,
 * oameni pe lista, sau au vorbit cu agentul in ultimele treizeci de zile.
 *
 * Randul se salveaza singur, cu butonul lui - nu exista un Save mare peste tot
 * tabelul: doua sute de firme intr-un singur buton inseamna ca o greseala pe
 * una le atinge pe toate.
 */
export function CompaniesTab({
  page,
  draft,
  onDraft,
  onSaveGlobal,
}: {
  page: AiAgentShell
  draft: GlobalDraft
  onDraft(next: GlobalDraft): void
  onSaveGlobal(): void
}) {
  const companies = page.companies
  const [level, setLevel] = useState(page.inflightView)
  const [levelNote, setLevelNote] = useState<Note | null>(null)

  const saveLevel = () => {
    setLevelNote({ text: '…' })
    void saveAiInflightView(level)
      .then((r) => setLevelNote(r.ok ? { text: 'Saved' } : { text: r.error ?? 'Failed', bad: true }))
      .catch((e: unknown) => setLevelNote({ text: failed(e), bad: true }))
  }

  return (
    <>
      <div className="panel panel-default">
        <div className="panel-body">
          <div className="aia-row">
            <label className="aia-row-label">Defaults</label>
            <div className="aia-row-field aia-company-defaults">
              <label className="aia-help">
                Investigation budget
                <input
                  type="number"
                  min={5}
                  max={2048}
                  className="form-control input-sm aia-num"
                  value={draft.maxSteps}
                  disabled={!page.ai.has_steps}
                  onChange={(e) => onDraft({ ...draft, maxSteps: Number(e.target.value) })}
                />
              </label>
              <label className="aia-help">
                Daily token budget
                <input
                  type="number"
                  min={0}
                  step={1000}
                  className="form-control input-sm aia-num"
                  value={draft.dailyBudget}
                  onChange={(e) => onDraft({ ...draft, dailyBudget: Number(e.target.value) })}
                />
              </label>
              <button type="button" className="btn btn-primary btn-sm" onClick={onSaveGlobal}>
                Save
              </button>
              <span className="aia-help" style={{ flexBasis: '100%' }}>
                what a company gets when it has not set its own. The investigation budget is tool rounds on one
                question; the token budget is per company, per day, and 0 means no cap.
                {!page.ai.has_steps ? <strong> The max_steps column is missing.</strong> : null}
              </span>
            </div>
          </div>

          <div className="aia-row">
            <label className="aia-row-label">In flight</label>
            <div className="aia-row-field">
              <select
                className="form-control input-sm aia-num"
                style={{ width: 220 }}
                value={level}
                onChange={(e) => setLevel(e.target.value)}
              >
                <option value="off">only my own work</option>
                <option value="admins">company admins see everyone&apos;s</option>
                <option value="all">everyone sees the company&apos;s</option>
              </select>
              <button type="button" className="btn btn-default btn-sm" onClick={saveLevel}>
                Save
              </button>
              <NoteText note={levelNote} />
              <span className="aia-help" style={{ flexBasis: '100%' }}>
                the Company button in the In flight window. Read only either way - nobody cancels or resends someone
                else&apos;s command from there. On a small team seeing each other&apos;s work is exactly right; on a
                provider with several clients in one portal it may not be.
              </span>
            </div>
          </div>
        </div>
      </div>

      {!companies || companies.rows.length === 0 ? (
        <div className="aia-note-warn">
          No company has used the agent yet, and none has its own settings. Give someone access under People and the
          company shows up here.
        </div>
      ) : (
        <>
          <table className="aia-companies">
            <tbody>
              <tr>
                <th>Company</th>
                <th>Access</th>
                <th>Investigation</th>
                <th>Tokens / day</th>
                <th>Used today</th>
                <th></th>
              </tr>
              {companies.rows.map((row) => (
                <CompanyRow
                  key={row.company_id}
                  row={row}
                  canAccess={companies.has_access}
                  canSteps={companies.has_steps}
                  canBudget={companies.has_budget}
                />
              ))}
            </tbody>
          </table>
          <span className="aia-help">
            0 in either box means the company takes the default above. Access off closes the agent for that company for
            everyone, the platform owner included - it is a decision about the client, not a right of the person
            looking.
            {!companies.has_budget || !companies.has_access ? (
              <strong> Some columns are missing - apply the schema from Change Log.</strong>
            ) : null}
          </span>
        </>
      )}
    </>
  )
}

function CompanyRow({
  row,
  canAccess,
  canSteps,
  canBudget,
}: {
  row: AiCompanyRow
  canAccess: boolean
  canSteps: boolean
  canBudget: boolean
}) {
  const [enabled, setEnabled] = useState(row.enabled)
  const [steps, setSteps] = useState(row.max_steps)
  const [budget, setBudget] = useState(row.daily_token_budget)
  const [note, setNote] = useState<Note | null>(null)

  const save = () => {
    setNote({ text: '…' })
    void saveAiCompanyLimits({
      company_id: row.company_id,
      enabled: enabled ? 1 : 0,
      max_steps: steps,
      daily_token_budget: budget,
    })
      .then((r) => setNote(r.ok ? { text: 'Saved' } : { text: r.error ?? 'Failed', bad: true }))
      .catch((e: unknown) => setNote({ text: failed(e), bad: true }))
  }

  return (
    <tr className="aia-company-row">
      <td>
        <strong>{row.name}</strong>
        <div className="aia-help">
          {row.people > 0
            ? `${row.people} ${row.people === 1 ? 'person' : 'people'} on the list`
            : 'everyone with the module right'}
        </div>
      </td>
      <td>
        <label className="aia-switch" title="off = nobody in this company can use the agent, not even a Global Admin">
          <input
            type="checkbox"
            checked={enabled}
            disabled={!canAccess}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <span className="aia-help">on</span>
        </label>
      </td>
      <td>
        <input
          type="number"
          min={0}
          max={2048}
          className="form-control input-sm aia-num"
          value={steps}
          placeholder="0"
          title="tool rounds on one question. 0 = the default above"
          disabled={!canSteps}
          onChange={(e) => setSteps(Number(e.target.value))}
        />
      </td>
      <td>
        <input
          type="number"
          min={0}
          step={1000}
          className="form-control input-sm aia-num"
          value={budget}
          placeholder="0"
          title="0 = the default above"
          disabled={!canBudget}
          onChange={(e) => setBudget(Number(e.target.value))}
        />
      </td>
      <td className="aia-help">{shortNum(row.tokens_today)}</td>
      <td className="aia-tier-cell-save">
        <button type="button" className="btn btn-default btn-sm" onClick={save}>
          Save
        </button>
        <NoteText note={note} />
      </td>
    </tr>
  )
}
